import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { GLOBALTYPES } from "../redux/actions/globalTypes";
import { getDataAPI, postDataAPI } from "../utils/fetchData";
import Adminnavbar from "./adminnavbar";
import Footer from "./footer";
import LoginRequest from "./loginRequest";
import Sidenavbar from "./sidenav";

// Constants
const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;

// Styles
const CONTAINER_STYLE = { backgroundColor: "#ffffff", padding: "30px" };
const DASHBOARD_ROW_STYLE = {
	paddingTop: "20px",
	paddingBottom: "20px",
	backgroundColor: "#A7D588",
};
const STATS_CARD_STYLE = { backgroundColor: "#24A2B3" };
const PENDING_CARD_STYLE = { backgroundColor: "#E96CB4" };
const TABLE_CONTAINER_STYLE = { paddingTop: "50px" };

const Groupmanagement = () => {
	const { auth } = useSelector((state) => state);
	const dispatch = useDispatch();

	// Modal state
	const [show, setShow] = useState(false);

	// Form state
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(false);

	// Data state
	const [groups, setGroups] = useState([]);
	const [pendingUsers, setPendingUsers] = useState([]);

	// Modal handlers
	const handleClose = useCallback(() => {
		setShow(false);
		setName("");
		setDescription("");
		setErrors({});
	}, []);

	const handleShow = useCallback(() => setShow(true), []);

	// Form validation
	const validateForm = useCallback(() => {
		const newErrors = {};

		if (!name.trim()) {
			newErrors.name = "Group name is required.";
		} else if (name.length > NAME_MAX_LENGTH) {
			newErrors.name = `Group name should not exceed ${NAME_MAX_LENGTH} characters.`;
		}

		if (!description.trim()) {
			newErrors.description = "Description is required.";
		} else if (description.length > DESCRIPTION_MAX_LENGTH) {
			newErrors.description = `Description should not exceed ${DESCRIPTION_MAX_LENGTH} characters.`;
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	}, [name, description]);

	// Form submission handler
	const handleSubmit = useCallback(
		async (e) => {
			e.preventDefault();
			if (!validateForm()) return;

			setLoading(true);
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });

			try {
				const res = await postDataAPI(
					"creategroup",
					{
						name,
						description,
					},
					auth.token
				);

				if (res.status === 200) {
					alert("Group created successfully.");
					window.location.reload();
				}
			} catch (err) {
				console.error("Error creating group:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: err.response?.data?.msg || "Error creating group" },
				});
			} finally {
				setLoading(false);
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		},
		[name, description, validateForm, auth.token, dispatch]
	);

	// Fetch data
	useEffect(() => {
		const fetchData = async () => {
			if (!auth.token) return;

			try {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });

				const [groupsRes, pendingUsersRes] = await Promise.all([
					getDataAPI(`getAllGroups`, auth.token),
					getDataAPI(`getAllPendingGroupAdmins`, auth.token),
				]);

				setGroups(groupsRes.data.groups || []);
				setPendingUsers(pendingUsersRes.data.groupAdmins || []);
			} catch (err) {
				console.error("Error fetching data:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: "Failed to fetch data" },
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		};

		fetchData();
	}, [auth.token, dispatch]);

	// Accept user handler
	const handleAcceptClick = useCallback(
		async (user) => {
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });

			try {
				const updateRes = await postDataAPI(
					"updategroupAdmins",
					{
						id: user.id,
						status: "accepted",
					},
					auth.token
				);

				if (updateRes.status === 200) {
					const addMemberRes = await postDataAPI(
						"addmemberingroup",
						{
							groupid: user.group.id,
						},
						auth.token
					);

					if (addMemberRes.status === 200) {
						window.location.reload();
					}
				}
			} catch (err) {
				console.error("Error accepting user:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: err.response?.data?.msg || "Error accepting user" },
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		},
		[auth.token, dispatch]
	);

	// Decline user handler
	const handleDeclineClick = useCallback(
		async (user) => {
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });

			try {
				const res = await postDataAPI(
					"updategroupAdmins",
					{
						id: user.id,
						status: "declined",
					},
					auth.token
				);

				if (res.status === 200) {
					window.location.reload();
				}
			} catch (err) {
				console.error("Error declining user:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: err.response?.data?.msg || "Error declining user" },
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		},
		[auth.token, dispatch]
	);

	// Memoized components
	const groupsRows = useMemo(() => {
		return groups.map((group, index) => (
			<tr key={group.id}>
				<td>{index + 1}</td>
				<td>{group.name}</td>
				<td>{group.description}</td>
			</tr>
		));
	}, [groups]);

	const pendingUsersRows = useMemo(() => {
		return pendingUsers.map((user, index) => (
			<tr key={user.id}>
				<td>{index + 1}</td>
				<td>{user.name}</td>
				<td>{user.group?.name || "N/A"}</td>
				<td>{user.email}</td>
				<td>{user.topics_of_interest || "N/A"}</td>
				<td>{user.mobile || "N/A"}</td>
				<td>{user.whatsapp || "N/A"}</td>
				<td>{user.expertise || "N/A"}</td>
				<td>{user.experience || "N/A"}</td>
				<td>{user.f_s_s_intersted || "N/A"}</td>
				<td>
					<button
						onClick={() => handleAcceptClick(user)}
						className="btn btn-success bg-success me-2">
						Accept
					</button>
					<button
						onClick={() => handleDeclineClick(user)}
						className="btn btn-danger bg-danger">
						Decline
					</button>
				</td>
			</tr>
		));
	}, [pendingUsers, handleAcceptClick, handleDeclineClick]);

	if (!auth.user) {
		return <LoginRequest />;
	}

	return (
		<>
			<Modal show={show} onHide={handleClose}>
				<div className="js">
					<div className="x-style" onClick={handleClose}>
						<span style={{ cursor: "pointer" }}>X</span>
					</div>
					<div className="js-boxIn">
						<div className="js-boxIn-content">
							<div className="ai-greenbox">
								<h4 className="js-greenbox-heading">Add Group</h4>
								<form onSubmit={handleSubmit}>
									<div className="js-inputbox">
										<span className="js-inputbox-input">
											<input
												type="text"
												placeholder="Group Name"
												value={name}
												onChange={(e) => setName(e.target.value)}
												maxLength={NAME_MAX_LENGTH}
											/>
										</span>
									</div>

									<p
										style={{
											visibility: errors.name ? "visible" : "hidden",
											color: "red",
										}}
										className="error">
										{errors.name || "Group name is required."}
									</p>

									<div className="js-inputbox">
										<span className="js-inputbox-input">
											<input
												type="text"
												placeholder="Description"
												value={description}
												onChange={(e) => setDescription(e.target.value)}
												maxLength={DESCRIPTION_MAX_LENGTH}
											/>
										</span>
									</div>

									<p
										style={{
											visibility: errors.description ? "visible" : "hidden",
											color: "red",
										}}
										className="error">
										{errors.description || "Description is required."}
									</p>

									<button
										type="submit"
										className="js-submitbtn"
										disabled={loading}>
										{loading ? "SUBMITTING..." : "SUBMIT"}
									</button>
								</form>
							</div>
						</div>
					</div>
				</div>
			</Modal>

			<Adminnavbar />
			<div style={CONTAINER_STYLE}>
				<div className="button-format">
					<div className="dashboard-text">Super Admin Dashboard</div>
					<button className="addinstructor-btn" onClick={handleShow}>
						+ ADD GROUP
					</button>
				</div>

				<div>
					<div className="row format">
						<Sidenavbar />
						<div className="col-8">
							<div className="row format" style={DASHBOARD_ROW_STYLE}>
								<div className="col text-format" style={STATS_CARD_STYLE}>
									<div className="text1">{groups.length}</div>
									<div className="text1">Groups</div>
								</div>
								<div className="col text-format" style={PENDING_CARD_STYLE}>
									<div className="text1">{pendingUsers.length}</div>
									<div className="text1">Pending Users</div>
								</div>
							</div>

							<div className="col mt-5">
								<h4 className="instructorlist-text">Groups List</h4>
								<Table>
									<thead id="pl">
										<tr>
											<th>S.No</th>
											<th>Name</th>
											<th>Description</th>
										</tr>
									</thead>
									<tbody>{groupsRows}</tbody>
								</Table>
							</div>
						</div>

						<div className="row" style={TABLE_CONTAINER_STYLE}>
							<div className="col">
								<h4 className="instructorlist-text">Pending Users</h4>
								<Table>
									<thead id="pl">
										<tr>
											<th>S.No</th>
											<th>Name</th>
											<th>Group name</th>
											<th>Email</th>
											<th>Topics of Interest</th>
											<th>Mobile</th>
											<th>WhatsApp</th>
											<th>Area of Expertise</th>
											<th>Years of Experience</th>
											<th>Food safety standard interested</th>
											<th>Action</th>
										</tr>
									</thead>
									<tbody>{pendingUsersRows}</tbody>
								</Table>
							</div>
						</div>
					</div>
				</div>
			</div>
			<Footer />
		</>
	);
};

export default Groupmanagement;
